import { useEffect, useMemo, useRef } from 'react'

import { useRouter } from 'next/router'

import { useHandleOrgChange } from '@/hooks/navigation/useHangleOrgChange'
import { useIsOnboarding } from '@/hooks/onboarding/useIsOnboarding'
import { useDefaultOrganization } from '@/hooks/organization/useDefaultOrganization'
import { useOrganizations } from '@/hooks/organization/useOrganizations'

import { useOrganizationStore } from '@/stores/OrganizationStore'

import { type UUID, isUUID } from '@/types/Id'

export const useActiveOrgId = () => {
  const router = useRouter()
  const { isOnboarding } = useIsOnboarding()
  const { activeOrgId: storedActiveOrgId, setActiveOrgId } =
    useOrganizationStore()
  const { data: defaultOrganization } = useDefaultOrganization({
    enabled: !isOnboarding,
  })
  const defaultOrganizationId = defaultOrganization?.id
  const { data: organizations } = useOrganizations()
  const { handleOrgChange } = useHandleOrgChange()

  const activeOrgId = isUUID(router.query.orgId)
    ? router.query.orgId
    : ('' as const)

  const handleOrgChangeRef = useRef(handleOrgChange)

  useEffect(() => {
    handleOrgChangeRef.current = handleOrgChange
  }, [handleOrgChange])

  const organizationIdsSet = useMemo(() => {
    if (organizations) {
      return new Set(organizations.map((org) => org.id))
    }
    return new Set<UUID>()
  }, [organizations])

  const haveLoadedOrganizations = organizations !== undefined
  const firstOrganizationId = organizations?.[0]?.id ?? ('' as const)
  const routeOrganizationIsAvailable =
    activeOrgId !== '' &&
    (!haveLoadedOrganizations || organizationIdsSet.has(activeOrgId))
  const storedOrganizationIsAvailable =
    storedActiveOrgId !== '' &&
    (!haveLoadedOrganizations || organizationIdsSet.has(storedActiveOrgId))
  const defaultOrganizationIsAvailable =
    defaultOrganizationId !== undefined &&
    defaultOrganizationId !== null &&
    (!haveLoadedOrganizations || organizationIdsSet.has(defaultOrganizationId))
  const fallbackOrganizationId = defaultOrganizationIsAvailable
    ? defaultOrganizationId
    : firstOrganizationId
  const repairOrganizationId = storedOrganizationIsAvailable
    ? storedActiveOrgId
    : fallbackOrganizationId
  const shouldRepairStoredActiveOrg =
    !isOnboarding &&
    haveLoadedOrganizations &&
    !routeOrganizationIsAvailable &&
    storedActiveOrgId !== '' &&
    !organizationIdsSet.has(storedActiveOrgId) &&
    repairOrganizationId !== '' &&
    repairOrganizationId !== storedActiveOrgId
  const shouldRepairRouteOrganization =
    !isOnboarding &&
    haveLoadedOrganizations &&
    activeOrgId !== '' &&
    !organizationIdsSet.has(activeOrgId) &&
    repairOrganizationId !== '' &&
    repairOrganizationId !== activeOrgId

  useEffect(() => {
    // do not set the active org id if the user is onboarding,
    // this causes a maximum recursion depth exceeded error;
    // TODO: figure out why
    if (isOnboarding) return

    if (routeOrganizationIsAvailable && activeOrgId !== storedActiveOrgId) {
      setActiveOrgId(activeOrgId)
    } else if (
      !activeOrgId &&
      fallbackOrganizationId &&
      fallbackOrganizationId !== storedActiveOrgId
    ) {
      setActiveOrgId(fallbackOrganizationId)
    }
  }, [
    activeOrgId,
    fallbackOrganizationId,
    isOnboarding,
    routeOrganizationIsAvailable,
    storedActiveOrgId,
    setActiveOrgId,
  ])

  useEffect(() => {
    if (shouldRepairStoredActiveOrg || shouldRepairRouteOrganization) {
      // reroute the user to the first valid org if they are not
      // a member of the org currently stored in client state
      if (repairOrganizationId !== storedActiveOrgId) {
        setActiveOrgId(repairOrganizationId)
      }
      handleOrgChangeRef.current(repairOrganizationId)
    }
  }, [
    repairOrganizationId,
    shouldRepairRouteOrganization,
    shouldRepairStoredActiveOrg,
    storedActiveOrgId,
    setActiveOrgId,
  ])

  const finalActiveOrgId = routeOrganizationIsAvailable
    ? activeOrgId
    : storedOrganizationIsAvailable
      ? storedActiveOrgId
      : fallbackOrganizationId

  return {
    activeOrgId: finalActiveOrgId,
  }
}
